/**
 * The ch03 feature manifest — season launch and initial allocation.
 *
 * Three routes, and the split is reqs/03's own:
 *
 * | Route | reqs/03 | The question it answers |
 * |---|---|---|
 * | `plan` | §1, §3.5 | What are we shipping, to which door, in which size, in which week — and do the carve-outs reconcile |
 * | `size-runs` | §3.2 C5, §8 | Does every served door open the season with a complete core run — gate D3 |
 * | `withholding` | §3.4, reqs/07 §3.6 | How much do we hold for the chase, and what is the bridge that justifies it |
 *
 * Every route declares `exports` and `drill`, because the manifest types make both
 * required on a data route — G2 tests *d* and *f* are type errors here rather than
 * census failures later.
 *
 * The drill targets follow reqs/07 §3.1's object chain rather than convenience: a
 * launch line drills to the door's size run, and the size run drills onward to the
 * in-season position, which is the same unit answered later in the season. That is the
 * chain reqs/08 §3 rank 4 merges into one engine, made navigable.
 */

import { defineFeature } from '../manifest';
import { LAUNCH_GUIDES } from './guides';

import { shipmentListExport, sizeRunExport, withholdingExport } from './exports';

export default defineFeature({
  slug: 'launch',
  titleKey: 'launch.title',
  navLabelKey: 'launch.navLabel',
  icon: 'split',  // brand guide p.14
  railGroup: 'future',  // bata-ui#132 — drawn, not offered yet (`disabled` still decides that)
  localeNamespace: 'launch',
  // Every route's guide and guided tour (ADR 9044) — one entry per route below, or the census fails.
  guides: LAUNCH_GUIDES,
  disabled: true,
  navigation: [
    { labelKey: 'launch.nav.plan', to: '/launch/plan', order: 30 },
    { labelKey: 'launch.nav.sizeRuns', to: '/launch/size-runs', order: 31 },
    { labelKey: 'launch.nav.withholding', to: '/launch/withholding', order: 32 },
  ],
  routes: [
    {
      path: 'plan',
      titleKey: 'launch.plan.title',
      urlParams: ['option'],
      component: () => import('./screens/LaunchPlanScreen'),
      surfaces: ['table', 'tile'],
      exports: {
        id: 'launch.plan.grid',
        formats: ['csv', 'xlsx'],
        resolve: shipmentListExport,
      },
      drill: { to: '/launch/size-runs', grain: 'store', alsoTo: ['/launch/withholding'] },
    },
    {
      path: 'size-runs',
      titleKey: 'launch.sizeRun.title',
      urlParams: ['option'],
      component: () => import('./screens/SizeRunScreen'),
      // `tile` only since F8 (2026-09-27): the fixture-backed heatmap and curve were removed.
      surfaces: ['tile'],
      exports: {
        id: 'launch.sizeRun.report',
        formats: ['csv', 'xlsx'],
        resolve: sizeRunExport,
      },
      drill: { to: '/launch/plan', grain: 'sku' },
    },
    {
      path: 'withholding',
      titleKey: 'launch.withholding.title',
      urlParams: ['option'],
      component: () => import('./screens/WithholdingScreen'),
      // `tile` only since F8 (2026-09-27): the fixture-backed sweep, bridge and
      // recommendation were removed; the realised hold-back is what remains.
      surfaces: ['tile'],
      exports: {
        id: 'launch.withholding.realised',
        formats: ['csv', 'xlsx'],
        resolve: withholdingExport,
      },
      drill: { to: '/launch/plan', grain: 'category' },
    },
  ],
});
